import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import {
  api, money, unitPrice, quantity, date, dateTime,
  DOC_TYPE_LABEL, STATUS_LABEL, STATUS_CLASS, PAYMENT_LABEL,
} from '../api.js';
import { useAuth, pustniZadarzhana } from '../App.jsx';
import { usePaymentMethods } from '../hooks.js';
import PurchaseEdit from './PurchaseEdit.jsx';
import SaleEdit from './SaleEdit.jsx';
import VoidReasonModal from '../components/VoidReasonModal.jsx';
import { DocumentServiceOrigin } from './Services.jsx';

// Вътрешни документи: без контрагент и без начин на плащане.
const VATRESHNI = ['transfer', 'revision', 'write_off'];

export default function DocumentView() {
  const { id } = useParams();
  const { user } = useAuth();
  const { methods, byCode } = usePaymentMethods();
  const navigate = useNavigate();
  /*
   * Отворен от „Стари продажби/доставки“ (30.10.2026, Светлана) — списъкът
   * подава адреса си (филтри + маркиран ред); „← Към списъка“ и „Затвори“
   * връщат точно там, вместо към нова продажба.
   */
  const otSpisak = useLocation().state?.otSpisak ?? null;
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [smyana, setSmyana] = useState(null);
  const [voiding, setVoiding] = useState(false);
  const [changingPayment, setChangingPayment] = useState(false);
  const [storning, setStorning] = useState(false);
  const [editingSale, setEditingSale] = useState(false);
  const [sendingToDelivery, setSendingToDelivery] = useState(false);

  // Редакция на приключена продажба — само бележките (документ + ред),
  // виж бележката до бутона „Редакция" по-долу за защо не и количества.
  const [notesEditing, setNotesEditing] = useState(false);
  const [noteDraft, setNoteDraft] = useState('');
  const [itemNoteDrafts, setItemNoteDrafts] = useState({});
  const [savingNotes, setSavingNotes] = useState(false);
  const [notesError, setNotesError] = useState(null);

  const load = () => api.get(`/api/documents/${id}`).then(setDoc).catch((e) => setError(e.message));

  useEffect(() => { setDoc(null); load(); }, [id]);

  function startNotesEditing() {
    setNoteDraft(doc.note ?? '');
    const map = {};
    for (const it of doc.items) map[it.id] = it.note ?? '';
    setItemNoteDrafts(map);
    setNotesError(null);
    setNotesEditing(true);
  }

  async function saveNotes() {
    setSavingNotes(true);
    setNotesError(null);
    try {
      if ((doc.note ?? '') !== noteDraft) {
        await api.patch(`/api/documents/${id}/note`, { note: noteDraft || null });
      }
      for (const it of doc.items) {
        const draft = itemNoteDrafts[it.id] ?? '';
        if ((it.note ?? '') !== draft) {
          await api.patch(`/api/documents/${id}/items/${it.id}/note`, { note: draft || null });
        }
      }
      setNotesEditing(false);
      await load();
    } catch (err) {
      setNotesError(err.message);
    } finally {
      setSavingNotes(false);
    }
  }

  async function confirm() {
    if (!window.confirm('Записване на документа? След това не може да се редактира — само сторниране.')) return;
    setBusy(true);
    try { await api.post(`/api/documents/${id}/confirm`); await load(); }
    catch (err) { setError(err.message); }
    finally { setBusy(false); }
  }

  async function storno(reason) {
    setBusy(true);
    try {
      const res = await api.post(`/api/documents/${id}/storno`, { reason });
      setStorning(false);
      navigate(`/documents/${res.id}`);
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  }

  /*
   * Анулиране на прекъсната продажба направо от списъка с документи.
   *
   * По т. 12 продажба с УНП не се трие — остава като запис с причина.
   * Достъпно е и за чужда чернова: анулирането не пипа чуждото — само
   * редакцията на редовете (виж „Довърши") е запазена за оператора,
   * започнал продажбата.
   */
  async function voidDraft(reason) {
    setBusy(true);
    try {
      await api.post(`/api/documents/${id}/void`, { reason });
      setVoiding(false);
      await load();
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  }

  /*
   * Изпраща задачата в Деливери (без дублиране — вече изпратената се
   * връща) и я отваря там в нов раздел, за поправка на ръка. Разделът се
   * отваря веднага при клика: отворен след отговора на сървъра, браузърът
   * го блокира като изскачащ прозорец.
   */
  /*
   * Забравена отметка „издай фактура" при записа — номерът се взема
   * сега (01.10.2026). Пита първо: номерът е от поредицата и не се връща.
   */
  async function issueInvoice() {
    let ochakvan = '';
    try { ochakvan = (await api.get('/api/documents/next-invoice-no')).expected; } catch { /* само за подсказка */ }
    if (!window.confirm(
      `Издаване на фактура за продажба № ${doc.doc_number}`
      + `${ochakvan ? ` — ще получи № ${ochakvan}` : ''}.

Номерът е от поредицата и не може да се върне. Продължи?`
    )) return;
    setBusy(true);
    try {
      const res = await api.post(`/api/documents/${id}/issue-invoice`);
      await load();
      setSmyana(`Издадена е фактура № ${res.invoiceNo}.`);
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  }

  async function sendToDelivery() {
    const razdel = window.open('', '_blank');
    setSendingToDelivery(true);
    try {
      const res = await api.post(`/api/documents/${id}/send-to-delivery`);
      if (razdel && res?.url) razdel.location.href = res.url;
      else if (res?.url) window.open(res.url, '_blank');
      await load();
    } catch (err) {
      razdel?.close();
      setError(err.message);
    } finally { setSendingToDelivery(false); }
  }

  if (error) return <div className="error">{error}</div>;
  if (!doc) return <div className="empty">Зареждане…</div>;

  const canWrite = user.role !== 'auditor';
  const canEdit = ['admin', 'manager', 'operator'].includes(user.role);
  // Сторно и редакции — и операторът (решение на Калин, 30.09.2026).
  const canStorno = ['admin', 'manager', 'operator'].includes(user.role);
  // Доставката се поправя; продажбата — не. Виж 037 и 038.
  const canEditPurchase = canEdit && doc.doc_type === 'purchase' && doc.status === 'confirmed';

  /*
   * Начинът на плащане се сменя, докато не е излизала бележка.
   *
   * „По банков път" и „ППП" не издават бон — там смяната е безобидна. При
   * „в брой", „ПОС" и „в брой на разсрочено" отпечатаното вече е излязло и
   * се отменя само със сторно. Сървърът пази същото правило; тук се решава
   * само дали да се показва копчето.
   */
  const bezBon = new Set(['bank', 'postal']);
  const canChangePayment = canEdit
    && doc.status === 'confirmed'
    && ((['sale', 'sale_return'].includes(doc.doc_type) && bezBon.has(doc.payment_method))
      // Доставка: и от „В брой“ — автоматичното плащане се сторнира само (07.10.2026).
      || doc.doc_type === 'purchase');

  /*
   * По Приложение № 29, т. 13 приключена продажба, за която е излязла
   * касова бележка, не се редактира — поправя се само със сторно; там
   * „Редакция" пипа единствено бележките.
   *
   * Чл. 3, ал. 1 от Наредбата обаче изважда плащането по банков път/ППП
   * от фискалния режим изцяло — за него никога не е излизал бон, затова
   * там „Редакция" отваря пълния режим (SaleEdit): количество, цена,
   * нови и анулирани редове, не само бележки. Виж 101_editable_sale_bez_bon.sql.
   */
  const canEditSaleFull = canEdit
    && ['sale', 'sale_return'].includes(doc.doc_type)
    && doc.status === 'confirmed'
    && bezBon.has(doc.payment_method);
  const canEditSaleNotes = canEdit
    && ['sale', 'sale_return'].includes(doc.doc_type)
    && doc.status === 'confirmed'
    && !canEditSaleFull;

  /*
   * Всеки документ се чете като фактура: по редовете цени и суми БЕЗ
   * ДДС, а данъкът — начислен най-отдолу върху общото. Така е и на
   * входящата, и на изходящата фактура.
   *
   * Числата се смятат от данъчната основа на реда, не от колоната
   * „цена". Тя пази различни неща: при продажба и при пренесените от
   * старата програма доставки — сумата С ДДС, при новите доставки —
   * без. Основата е вярна навсякъде и е единствената обща мярка.
   */
  const lineSum = (it) => it.line_subtotal;

  return (
    <>
      <div className="page-head">
        <h1>{DOC_TYPE_LABEL[doc.doc_type]} № {doc.doc_number}</h1>
        <span className={`badge ${STATUS_CLASS[doc.status]}`}>{STATUS_LABEL[doc.status]}</span>
        <div className="spacer" />
        {otSpisak && <button onClick={() => navigate(otSpisak)}>← Към списъка</button>}
        {doc.status === 'draft' && doc.doc_type === 'sale' && canWrite && (
          <Link to={`/documents/${doc.id}/continue`} className="button">Довърши</Link>
        )}
        {doc.status === 'draft' && canWrite && (
          <button className="primary" onClick={confirm} disabled={busy}>Запис</button>
        )}
        {doc.status === 'draft' && doc.doc_type === 'sale' && canWrite && (
          <button className="danger" onClick={() => setVoiding(true)} disabled={busy}>
            Анулирай
          </button>
        )}
        {canEditPurchase && !editing && (
          <button onClick={() => setEditing(true)} disabled={busy}>Редакция</button>
        )}
        {canEditSaleFull && !editingSale && (
          // Продажбата се редактира в екрана на новата продажба — наличности,
          // партиди, цени, тонер/чип (02.10.2026). Връщането — тук, както досега.
          <button onClick={() => (doc.doc_type === 'sale'
            ? navigate(`/documents/${doc.id}/edit`, otSpisak ? { state: { otSpisak } } : undefined)
            : setEditingSale(true))} disabled={busy}>Редакция</button>
        )}
        {canEditSaleNotes && !notesEditing && (
          <button onClick={startNotesEditing} disabled={busy}>Редакция</button>
        )}
        {/* Корекция на записано прехвърляне (13.10.2026) — в екрана му. */}
        {doc.status === 'confirmed' && doc.doc_type === 'transfer' && canStorno && (
          <button onClick={() => navigate(`/transfer?edit=${doc.id}`)} disabled={busy}>
            Корекция
          </button>
        )}
        {doc.status === 'confirmed' && canStorno
          && !['sale_return', 'purchase_return'].includes(doc.doc_type) && (
          <button className="danger" onClick={() => setStorning(true)} disabled={busy}>
            Сторно
          </button>
        )}
        {doc.status === 'confirmed' && doc.doc_type === 'sale' && !doc.invoice_no
          && doc.partner_id && canWrite && (
          <button onClick={issueInvoice} disabled={busy}
            title="Продажбата е записана без фактура — взема следващия номер на фактура">
            Издай фактура
          </button>
        )}
        {doc.status === 'confirmed' && doc.doc_type === 'sale' && canWrite && (
          /* Само на ръка (01.10.2026). Отваря задачата в Деливери — и
             вече изпратената, без да я дублира — за поправка там. */
          <button onClick={sendToDelivery} disabled={sendingToDelivery}
            title={doc.delivery_notified_at
              ? `Изпратено в Деливери на ${dateTime(doc.delivery_notified_at)} — отваря задачата там`
              : 'Създава задача за разнос в Деливери и я отваря там'}>
            {doc.delivery_notified_at ? 'Отвори в Деливери' : 'Изпрати в Деливери'}
          </button>
        )}
        <a href={`/print/document/${doc.id}`} target="_blank" rel="noreferrer"
          onClick={() => {
            /* „Печат" пуска задържания служебен бон — касовият вече е откъснат. */
            pustniZadarzhana();
            api.post('/api/cash/fiscal-queue/flush-service-out', {}).catch(() => {});
          }}>
          <button>Печат</button>
        </a>
      </div>

      {doc.doc_type === 'sale' && <DocumentServiceOrigin documentId={doc.id} />}

      <div className="card">
        <div className="card-body">
          <div className="doc-grid">
            <F k="Дата" v={date(doc.doc_date)} />
            <F k="Съставен" v={dateTime(doc.doc_datetime)} />
            {!VATRESHNI.includes(doc.doc_type) && <F k="Контрагент" v={doc.partner_name ?? 'на каса'} />}
            {doc.partner_eik && <F k="ЕИК" v={doc.partner_eik} />}
            {doc.partner_vat && <F k="ДДС номер" v={doc.partner_vat} />}
            <F k="Склад" v={doc.warehouse_name ?? '—'} />
            {doc.warehouse_to_name && <F k="Към склад" v={doc.warehouse_to_name} />}
            {/* Прехвърляне, ревизия и брак са вътрешни — нямат клиент и плащане. */}
            {!VATRESHNI.includes(doc.doc_type) && (
              <F
                k="Плащане"
                v={
                  <>
                    {byCode[doc.payment_method]?.name ?? PAYMENT_LABEL[doc.payment_method]}
                    {byCode[doc.payment_method]?.issuesFiscalReceipt === false && (
                      <span className="badge muted" style={{ marginLeft: 6 }}>без касова бележка</span>
                    )}
                    {(notesEditing || editingSale || (canEditSaleFull && doc.doc_type === 'sale') || doc.doc_type === 'purchase') && canChangePayment && (
                      <button
                        className="link"
                        style={{ marginLeft: 8 }}
                        onClick={() => setChangingPayment(true)}
                      >
                        смени
                      </button>
                    )}
                  </>
                }
              />
            )}
            {doc.invoice_no && <F k="Фактура" v={`№ ${doc.invoice_no} / ${date(doc.invoice_date)}`} />}
            {doc.unp && <F k="УНП" v={doc.unp} />}
            {doc.fiscal_receipt && <F k="Фискален бон" v={doc.fiscal_receipt} />}
            <F k="Съставил" v={doc.created_by_name ?? '—'} />
            {doc.storno_of_number && (
              <F k="Сторно на" v={doc.storno_of_number} />
            )}
          </div>
          {notesEditing ? (
            <label className="field" style={{ marginTop: 16 }}>
              Бележка към документа
              <textarea
                rows={3}
                value={noteDraft}
                onChange={(e) => setNoteDraft(e.target.value)}
                placeholder="няма бележка"
              />
            </label>
          ) : (
            // При SaleEdit бележката се пише в самия него — тук не се
            // повтаря, за да няма две полета за едно и също нещо.
            !editingSale && doc.note
              && <p className="hint" style={{ marginTop: 16, whiteSpace: 'pre-line' }}>{doc.note}</p>
          )}
          {notesEditing && (
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 10 }}>
              <button className="primary" onClick={saveNotes} disabled={savingNotes}>
                {savingNotes ? 'Записване…' : 'Запази'}
              </button>
              <button onClick={() => setNotesEditing(false)} disabled={savingNotes}>Откажи</button>
              {notesError && <span className="error">{notesError}</span>}
            </div>
          )}
        </div>
      </div>

      {smyana && <div className="notice">{smyana}</div>}

      {editing && (
        <PurchaseEdit
          doc={doc}
          onCancel={() => setEditing(false)}
          onDone={() => { setEditing(false); load(); }}
        />
      )}

      {editingSale && (
        <SaleEdit
          doc={doc}
          onCancel={() => setEditingSale(false)}
          onDone={() => { setEditingSale(false); load(); }}
        />
      )}

      <div className="card" style={{ display: (editing || editingSale) ? "none" : undefined }}>
        <div className="card-head">Редове</div>
        <div className="table-wrap">
          <table className="compact">
            <thead>
              <tr>
                <th style={{ width: 40 }}>№</th>
                <th>Наименование</th>
                <th>Бележка на стоката</th>
                <th>Капацитет</th>
                <th>Бележка на реда</th>
                <th className="num">Количество</th>
                <th className="num">Ед. цена без ДДС</th>
                <th className="num">ДДС</th>
                <th className="num">Сума без ДДС</th>
              </tr>
            </thead>
            <tbody>
              {doc.items.map((it) => {
                /*
                 * Анулиран ред се вижда, но не се брои.
                 *
                 * Т. 12 иска анулираните редове да се пазят в базата — но
                 * не бива да изглеждат като част от сметката: зачертани и
                 * приглушени, а сумата им не влиза в „Сума без ДДС" под
                 * таблицата (тя вече е сметната само от активните).
                 */
                const anuliran = Number(it.is_cancelled) === 1;
                const style = anuliran ? { textDecoration: 'line-through' } : undefined;
                return (
                  <tr key={it.id} className={anuliran ? 'muted' : undefined}>
                    <td className="muted">{it.line_no}</td>
                    <td style={style}>
                      {it.product_id
                        ? <Link to={`/products/${it.product_id}`}>{it.description}</Link>
                        : it.description}
                      {Number(it.is_gift) === 1 && (
                        <span className="badge muted" style={{ marginLeft: 6 }}>подарък</span>
                      )}
                      {anuliran && (
                        <span className="badge muted" style={{ marginLeft: 6 }}>анулиран</span>
                      )}
                    </td>
                    <td className="muted" style={style}>{it.product_note ?? '—'}</td>
                    <td className="muted" style={style}>{it.product_capacity ?? '—'}</td>
                    <td style={style}>
                      {notesEditing
                        ? (
                          <input
                            type="text"
                            value={itemNoteDrafts[it.id] ?? ''}
                            onChange={(e) => setItemNoteDrafts((m) => ({ ...m, [it.id]: e.target.value }))}
                            style={{ width: '100%' }}
                          />
                        )
                        : (it.note ?? '—')}
                    </td>
                    <td className="num" style={style}>{quantity(it.quantity)} {it.unit}</td>
                    <td className="num" style={style}>
                      {Number(it.is_gift) === 1 ? '—' : unitPrice(it, it.currency)}
                    </td>
                    <td className="num muted" style={style}>
                      {Number(it.is_gift) === 1 ? '' : `${Number(it.vat_rate)}%`}
                    </td>
                    <td className="num" style={style}>{money(lineSum(it), it.currency)}</td>
                  </tr>
                );
              })}
              {/* Отстъпката за цялата фактура е ред от нея и стои
                  последна, за да се сверява документът ред по ред с
                  хартията. Числото е каквото пише на фактурата — без
                  ДДС; данъкът се смята върху свалената основа и стои
                  под таблицата. */}
              {Number(doc.invoice_discount) > 0 && (
                <tr>
                  <td className="muted">{doc.items.length + 1}</td>
                  <td>Отстъпка по фактурата</td>
                  <td className="muted">—</td>
                  <td className="muted">—</td>
                  <td className="muted">—</td>
                  <td className="num muted">—</td>
                  <td className="num muted">—</td>
                  <td className="num muted">—</td>
                  <td className="num">−{money(doc.invoice_discount, doc.currency)}</td>
                </tr>
              )}
              {doc.items.length === 0 && (
                <tr><td colSpan={9} className="empty">Документът няма редове</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="card-body">
          <div className="totals">
            <div><span className="muted">Данъчна основа</span><span>{money(doc.subtotal, doc.currency)}</span></div>
            <div><span className="muted">ДДС</span><span>{money(doc.vat_total, doc.currency)}</span></div>
            <div className="grand"><span>Общо</span><span>{money(doc.total, doc.currency)}</span></div>
            {Number(doc.paid_amount) > 0 && (
              <div><span className="muted">Платено</span><span>{money(doc.paid_amount, doc.currency)}</span></div>
            )}
            {Number(doc.paid_amount) < Number(doc.total) && (
              <div><span className="muted">Остава</span>
                <span style={{ color: 'var(--danger)' }}>
                  {money(Number(doc.total) - Number(doc.paid_amount), doc.currency)}
                </span>
              </div>
            )}
          </div>

          {/* „Затвори" — край на тази продажба/доставка и направо нова.
              Пуска и задържания служебен бон, както „Печат" (01.10.2026). */}
          {['sale', 'purchase'].includes(doc.doc_type) && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
              <button className="primary" style={{ minWidth: 140 }}
                onClick={() => {
                  // Само пуска задържането; самото служебно извеждане го
                  // праща смяната на страницата (App.jsx). Двете извиквания
                  // тук и там тръгваха в една милисекунда и бонът излизаше
                  // двойно (07.10.2026, 0000040630 и 0000040623).
                  pustniZadarzhana();
                  navigate(otSpisak ?? `/documents/new/${doc.doc_type}`);
                }}>
                Затвори
              </button>
            </div>
          )}

          {/* Транспортът „за наша сметка" не е ред от фактурата и не
              влиза в сумата ѝ — стои най-отдолу, за да се вижда какъв е
              бил, без да се бърка с дължимото. */}
          {Number(doc.transport_cost) > 0 && (
            <p className="hint" style={{ marginBottom: 0 }}>
              Транспорт за наша сметка <b>{money(doc.transport_cost, doc.currency)}</b> — не
              влиза в сумата на фактурата, ляга по себестойността на редовете.
            </p>
          )}
        </div>
      </div>

      {doc.payments.length > 0 && (
        <div className="card">
          <div className="card-head">Плащания</div>
          <table className="compact">
            <thead>
              <tr><th>Номер</th><th>Дата</th><th>Начин</th><th className="num">Сума</th></tr>
            </thead>
            <tbody>
              {doc.payments.map((p, i) => (
                <tr key={i}>
                  <td>{p.payment_number}</td>
                  <td>{date(p.payment_date)}</td>
                  <td className="muted">{PAYMENT_LABEL[p.method]}</td>
                  <td className="num">{money(p.amount, doc.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {voiding && (
        <VoidReasonModal
          unp={doc.unp}
          docNumber={doc.doc_number}
          onClose={() => setVoiding(false)}
          onConfirm={voidDraft}
        />
      )}

      {changingPayment && (
        <PaymentMethodModal
          doc={doc}
          methods={methods}
          onClose={() => setChangingPayment(false)}
          onChanged={async (msg) => { setSmyana(msg); await load(); }}
        />
      )}

      {storning && (
        <VoidReasonModal
          title={`Сторно на документ № ${doc.doc_number}`}
          hint={null}
          confirmLabel="Сторно"
          onClose={() => setStorning(false)}
          onConfirm={storno}
        />
      )}

      {doc.manual_receipt && !doc.manual_receipt.confirmedAt && (
        <ManualReceiptModal doc={doc} onDone={load} />
      )}
    </>
  );
}

/**
 * Преходният период: бонът се бие на ръка на апарата.
 *
 * Излиза върху документа, докато операторът не потвърди с „Готово" —
 * след продажба, сторно или връщане, по който и път да се е стигнало.
 * Едва потвърждението пуска служебната сума при разсрочено плащане,
 * така че служебният бон излиза след касовия, а не преди него.
 */
function ManualReceiptModal({ doc, onDone }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const storno = doc.manual_receipt.kind === 'storno';
  const suma = Math.abs(Number(doc.total));

  async function gotovo() {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/documents/${doc.id}/receipt-printed`);
      await onDone();
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop">
      <div className="modal" role="dialog" aria-modal="true" style={{ maxWidth: 560 }}>
        <div className="card-head">{storno ? 'Сторно бон на ръка' : 'Касов бон на ръка'}</div>
        <div className="card-body">
          {error && <div className="error">{error}</div>}
          <div style={{ fontSize: 20, marginBottom: 8 }}>
            Изпечатай на апарата {storno ? 'сторно бон' : 'касов бон'}:{' '}
            <strong>{money(suma, doc.currency)}</strong>
            {' — '}<strong>{doc.manual_receipt.methodName}</strong>
          </div>
          <div className="muted">
            {storno && doc.storno_of_number ? `по продажба № ${doc.storno_of_number} · ` : ''}
            документ № {doc.doc_number} · {doc.items?.filter((i) => !Number(i.is_cancelled)).length ?? 0} реда
          </div>
          {storno && (
            <p className="hint" style={{ marginBottom: 0 }}>
              Номерът, датата и часът на касовия бон се въвеждат на самия апарат.
            </p>
          )}
        </div>
        <div className="modal-foot">
          <button className="primary" disabled={busy} onClick={gotovo}>
            {busy ? 'Записване…' : 'Готово — изпечатан е'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Смяна на начина на плащане — падащо меню вместо номерата в prompt.
 *
 * При „в брой" и „ПОС" устройството пуска бележка веднага щом смяната
 * мине — това е необратимо и трябва да се знае предварително, а не да
 * се открие после на лентата.
 */
export function PaymentMethodModal({ doc, methods, onClose, onChanged, beforeChange }) {
  const options = methods.filter((m) => m.code !== doc.payment_method);
  const [code, setCode] = useState(options[0]?.code ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const chosen = methods.find((m) => m.code === code);

  async function confirm() {
    if (!code) return;
    setBusy(true);
    setError(null);
    try {
      if (beforeChange) await beforeChange();
      const r = await api.patch(`/api/documents/${doc.id}/payment-method`,
        { paymentMethod: code });
      await onChanged(r.unchanged ? 'Начинът е същият.' : `Начинът е сменен на „${r.name}".`);
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onMouseDown={(e) => {
      if (e.target === e.currentTarget) onClose();
    }}>
      <div className="modal" role="dialog" aria-modal="true">
        <div className="card-head">Смяна на начина на плащане</div>
        <div className="card-body">
          <p className="hint" style={{ marginTop: 0 }}>
            Сегашният начин е „{doc.payment_method_name ?? doc.payment_method}".
          </p>
          {error && <div className="error" style={{ marginBottom: 10 }}>{error}</div>}
          <label className="field">
            Нов начин
            <select value={code} onChange={(e) => setCode(e.target.value)}>
              {options.map((m) => <option key={m.code} value={m.code}>{m.name}</option>)}
            </select>
          </label>
          {doc.doc_type === 'purchase' ? (
            <div className="hint" style={{ marginTop: 10 }}>
              {code === 'cash'
                ? 'Доставката ще се отбележи платена — сумата излиза от касата.'
                : doc.payment_method === 'cash'
                  ? 'Плащането от касата се сторнира — сумата се връща в касата, а задължението към доставчика остава за плащане.'
                  : 'Задължението към доставчика остава за плащане.'}
            </div>
          ) : chosen?.issuesFiscalReceipt && (
            <div className="warning" style={{ marginTop: 10 }}>
              При записа касовият апарат ще пусне бележка. Това не се отменя с редакция.
            </div>
          )}
          {beforeChange && (
            <div className="hint" style={{ marginTop: 10 }}>
              Редовете на екрана се записват първо — бележката е за тях.
            </div>
          )}
        </div>
        <div className="modal-foot">
          <button onClick={onClose} disabled={busy}>Откажи</button>
          <button className="primary" disabled={busy || !code} onClick={confirm}>
            Смени
          </button>
        </div>
      </div>
    </div>
  );
}

function F({ k, v }) {
  return <div><div className="k">{k}</div><div className="v">{v}</div></div>;
}
